import { useEffect } from "react";

interface SEOHelmetProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  ogImage?: string;
  schemaData?: Record<string, unknown>;
}

export default function SEOHelmet({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = "website",
  ogImage,
  schemaData,
}: SEOHelmetProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("keywords", keywords);

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) {
      canonicalEl.setAttribute("href", canonicalUrl);
    } else {
      const link = document.createElement("link");
      link.rel = "canonical";
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }

    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", ogType, true);
    setMeta("og:url", canonicalUrl, true);
    if (ogImage) {
      setMeta("og:image", ogImage, true);
    }

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    if (ogImage) {
      setMeta("twitter:image", ogImage);
    }

    if (schemaData) {
      const existingScript = document.getElementById("schema-jsonld");
      if (existingScript) {
        existingScript.remove();
      }
      const script = document.createElement("script");
      script.id = "schema-jsonld";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(schemaData, null, 2);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById("schema-jsonld");
      if (script) script.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, schemaData]);

  return null;
}